import { jobSectionMap } from '../jobSectionMap';
import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  jobDetails: {
    generalProject: [],
    generalStation: [],
    soldToShipTo: [],
    priceBreakdown: [],
    ownersManual: [],
    hcRequirements: [],
    skidConstruction: [],
    pumpManifoldPiping: [],
    stationEnclosure: [],
    dropPipeComments: [],
    dropPipes: [],
    levelControls: [],
    fillSources: [],
    pumps: [],
    pumpDetails: [],
    waterTreatment: [],
    inletScreen: [],
    inletStrainer: [],
    dischargeStrainer: [],
    dischargeFilter: [],
    roSystem: [],
    uvSystem: [],
    fertigation: [],
    skyHarvester: [],
    shipLoose: [],
    unregulatedPower: [],
    unregulatedBreakers: [],
    regulatedPower: [],
    regulatedBreakers: [],
    sequenceOfOperations: [],
    electricalOptions: [],
    telemetry: [],
    telemetryAccessories: [],
    selectedPumpOptionList: [],
    customOptions: [],
    warningReport: [],
    changedOptions: [],
    controlLogicOnly: [],
    shipEarlyParent: [],
    shipEarlyItems: [],
    changeOrderParent: [],
    changeOrderDetails: [],
  },
};

const jobdetailsslice = createSlice({
  name: "jobdetails",
  initialState,
  reducers: {
    replaceRealtimeSections: (state, action) => {
      // Replace only known sections explicitly present in the validated snapshot.
      for (const section of Object.values(jobSectionMap)) {
        if (Array.isArray(action.payload[section])) state.jobDetails[section] = action.payload[section];
      }
    },
    setJobDetail: (state, action) => {
      const { key, value, section, zone } = action.payload;
      if (!state.jobDetails[section]) {
        state.jobDetails[section] = [];
      }
      let targetObject;
      if (zone) {
        targetObject = state.jobDetails[section].find(obj => obj.Zone === zone && obj.ID === key);
      } else {
        targetObject = state.jobDetails[section].find(obj => obj.ID === key);
      }
      if (targetObject) {
        Object.assign(targetObject, value); // Update the target object with new values
      } else {
        state.jobDetails[section].push({ Zone: zone, ID: key, ...value });
      }
    },
    setMultipleJobDetails: (state, action) => {
      const { section, updates } = action.payload;
      if (!state.jobDetails[section]) {
        state.jobDetails[section] = [];
      }
      
      // Log change order updates for debugging
      if (section === 'changeOrderParent' || section === 'changeOrderDetails') {
        console.log(`setMultipleJobDetails: ${section} - Updating ${updates.length} items`);
      }
      
      let hasChanges = false;
      
      updates.forEach(update => {
        const { Zone, zone, ID, ...rest } = update;
        const actualZone = Zone !== undefined ? Zone : zone;
        
        // Find existing object - match by Zone and ID if Zone exists, otherwise just ID
        let targetObject;
        if (actualZone !== undefined) {
          targetObject = state.jobDetails[section].find(obj => obj.Zone === actualZone && obj.ID === ID);
        } else {
          targetObject = state.jobDetails[section].find(obj => obj.ID === ID);
        }
        
        if (targetObject) {
          Object.keys(rest).forEach(key => {
            if (targetObject[key] !== rest[key]) {
              targetObject[key] = rest[key]; // Only update changed values
              hasChanges = true;
            }
          });
        } else {
          const newObject = actualZone !== undefined ? { Zone: actualZone, ID, ...rest } : { ID, ...rest };
          state.jobDetails[section].push(newObject);
          hasChanges = true;
        }
      });
      
      // Force new array reference if changes were made to trigger React re-renders
      if (hasChanges) {
        state.jobDetails[section] = [...state.jobDetails[section]];
      }
      
      // Remove duplicates - handle both zoned and non-zoned records
      const beforeCount = state.jobDetails[section].length;
      state.jobDetails[section] = state.jobDetails[section].filter((obj, index, self) => {
        if (obj.Zone !== undefined) {
          // For zoned records, match by Zone and ID
          return index === self.findIndex((t) => t.Zone === obj.Zone && t.ID === obj.ID);
        } else {
          // For non-zoned records (like change orders), match by ID only
          return index === self.findIndex((t) => t.ID === obj.ID && t.Zone === undefined);
        }
      });
      const afterCount = state.jobDetails[section].length;
      
      if (section === 'changeOrderParent' || section === 'changeOrderDetails') {
        console.log(`setMultipleJobDetails: ${section} - After deduplication: ${beforeCount} -> ${afterCount} items`);
        console.log(`setMultipleJobDetails: ${section} - Final data:`, state.jobDetails[section]);
      }
    },
    clearJobDetails: (state) => {
      state.jobDetails = initialState.jobDetails;
    },
    clearSectionJobDetails: (state, action) => {
      const section = action.payload;
      state.jobDetails[section] = [];
    },
    removeJobDetail: (state, action) => {
      const { section, id, zone } = action.payload;
      if (!state.jobDetails[section]) return;

      if (zone !== undefined) {
        state.jobDetails[section] = state.jobDetails[section].filter(
          (obj) => !(obj.Zone === zone && obj.ID === id)
        );
      } else {
        state.jobDetails[section] = state.jobDetails[section].filter(
          (obj) => obj.ID !== id
        );
      }
    },
  },
});

export const { replaceRealtimeSections, setJobDetail, setMultipleJobDetails, clearJobDetails, clearSectionJobDetails, removeJobDetail } = jobdetailsslice.actions;
export const selectJobDetails = (state) => state.jobDetails?.jobDetails || {};
export default jobdetailsslice.reducer;
